import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { useMemo } from "react";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import { useItemsInfoQueries } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { useCombinationArticle } from "@coworker/app/src/components/RoomSettings/hooks/useCombinationArticle";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";

const NO_ITEM_NOS: string[] = [];

type UseAllAttachedItemsResult = {
  items: Item[];
  isFetchingItems: boolean;
  isErrorFetchingItems: boolean;
};

export const useAllAttachedItems = (
  itemNosAndType: ItemNoAndType[] | Loading
): UseAllAttachedItemsResult => {
  const storeId = useStoreId();
  const countryId = useCountryId();

  const regularItemNos = useMemo(() => {
    if (isLoading(itemNosAndType)) {
      return NO_ITEM_NOS;
    }
    return itemNosAndType
      .filter(({ type }) => type !== "SCI")
      .map(({ no }) => no);
  }, [itemNosAndType]);

  const localCombinationItemNos = useMemo(() => {
    if (isLoading(itemNosAndType)) {
      return NO_ITEM_NOS;
    }
    return itemNosAndType
      .filter(({ type }) => type === "SCI")
      .map(({ no }) => no);
  }, [itemNosAndType]);

  const itemQueries = useItemsInfoQueries(regularItemNos);

  const localCombinationItemsQuery = useCombinationArticle(
    storeId,
    countryId,
    localCombinationItemNos.toSorted().join(",")
  );

  const isFetchingItems = useMemo(() => {
    return (
      itemQueries.some(({ isFetching }) => isFetching) ||
      localCombinationItemsQuery.isFetching
    );
  }, [itemQueries, localCombinationItemsQuery.isFetching]);

  const isErrorFetchingItems = useMemo(() => {
    return (
      itemQueries.some(({ isError }) => isError) ||
      localCombinationItemsQuery.isError
    );
  }, [itemQueries, localCombinationItemsQuery.isError]);

  const items = useMemo(() => {
    const regularItems = itemQueries
      .map(({ data }) => data)
      .filter(Boolean) as Item[];
    const localCombinationItems =
      (localCombinationItemsQuery.data as Item[]) ?? [];
    return [...regularItems, ...localCombinationItems];
  }, [itemQueries, localCombinationItemsQuery.data]);

  return {
    items,
    isFetchingItems,
    isErrorFetchingItems,
  };
};
